import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import _ from "lodash";
import config from "../../config";
import docTypeFields from "../../models/DocTypeFields";
import {
    buildRetailDraftOrderFilters,
    buildRetailDraftSalesQuantityIndex,
    buildRetailStockIndex,
    buildRetailWarehouseScope,
    getAccessibleCompanyNames,
    getRetailSellingWarehouses,
} from "../utils/companyStockUtils";
import {
    buildSeparatorTolerantRetailSearchTerm,
    filterRetailItemsByCompactCode,
} from "../utils/retailSearchUtils";

const fetchMenuItems = () => {
    return axios.get(`/method/restaurant_management.api.get_menu_items`);
};

const getPreferredRate = (...values) => {
    const finiteValues = values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
    const positiveValue = finiteValues.find((value) => value > 0);
    return positiveValue ?? finiteValues[0] ?? 0;
};

const getAttributeValue = (attributes, attributeName) => {
    if (!Array.isArray(attributes)) return "";
    const normalizedName = String(attributeName || "").toLowerCase();
    const match = attributes.find((entry) => {
        const label = String(entry?.attribute || entry?.attribute_name || "")
            .trim()
            .toLowerCase();
        return label === normalizedName;
    });
    return match?.attribute_value || match?.value || "";
};

const fetchRetailItemRows = async (itemCodes) => {
    const codes = [...new Set((itemCodes || []).filter(Boolean))];
    if (codes.length === 0) return {};

    const fields = [
        "name",
        "item_code",
        "item_name",
        "variant_of",
        "image",
        "item_image",
        "has_variants",
    ];

    const indexRows = (rows) =>
        (rows || []).reduce((acc, row) => {
            if (row?.item_code) acc[row.item_code] = row;
            if (row?.name) acc[row.name] = row;
            return acc;
        }, {});

    try {
        // Builder uses this endpoint for Item rows because it includes child
        // tables such as `attributes`; prefer it when available.
        const responses = await Promise.all(
            _.chunk(codes, 80).map((chunk) =>
                axios.get("method/custom_api.api.get_list_with_children", {
                    params: {
                        doctype: "Item",
                        fields: JSON.stringify(fields),
                        filters: JSON.stringify([["name", "in", chunk]]),
                        limit: 0,
                    },
                })
            )
        );

        return indexRows(responses.flatMap((response) => response.data?.message || []));
    } catch (error) {
        console.warn("Failed to fetch retail item rows with children", error);
    }

    try {
        const responses = await Promise.all(
            _.chunk(codes, 80).map((chunk) =>
                axios.get("method/frappe.client.get_list", {
                    params: {
                        doctype: "Item",
                        fields: JSON.stringify(fields),
                        filters: JSON.stringify([["name", "in", chunk]]),
                        limit_page_length: 0,
                    },
                })
            )
        );

        return indexRows(responses.flatMap((response) => response.data?.message || []));
    } catch (error) {
        console.warn("Failed to fetch retail item rows", error);
        return {};
    }
};

const fetchVariantAttributes = async (itemIdentifiers) => {
    const identifiers = [...new Set((itemIdentifiers || []).filter(Boolean))];
    if (identifiers.length === 0) return {};

    try {
        const responses = await Promise.all(
            _.chunk(identifiers, 80).map((chunk) =>
                axios.get("resource/Item Variant Attribute", {
                    params: {
                        fields: JSON.stringify([
                            "parent",
                            "attribute",
                            "attribute_value",
                        ]),
                        filters: JSON.stringify([["parent", "in", chunk]]),
                        limit_page_length: 0,
                    },
                })
            )
        );

        return responses
            .flatMap((response) => response.data?.data || [])
            .reduce((acc, row) => {
                if (!row?.parent) return acc;
                if (!acc[row.parent]) acc[row.parent] = [];
                acc[row.parent].push({
                    attribute: row.attribute,
                    attribute_value: row.attribute_value,
                });
                return acc;
            }, {});
    } catch (error) {
        console.warn("Failed to fetch item variant attributes", error);
        return {};
    }
};

const emptyRetailStockIndex = () => ({
    currentWarehouseByItemCode: {},
    companyActualQtyByItemCode: {},
    companyStockAvailable: false,
});

const fetchRetailDraftSalesQuantityIndex = async ({ tableName, company }) => {
    const filters = buildRetailDraftOrderFilters({ tableName, company });
    if (!filters.length) return {};

    const { data: ordersData } = await axios.get(
        "method/frappe.client.get_list",
        {
            params: {
                doctype: "Table Order",
                fields: JSON.stringify(["name"]),
                filters: JSON.stringify(filters),
                limit_page_length: 0,
            },
        }
    );
    const orderNames = (ordersData?.message || [])
        .map((order) => order?.name)
        .filter(Boolean);
    if (orderNames.length === 0) return {};

    const { data: itemsData } = await axios.get(
        "method/frappe.client.get_list",
        {
            params: {
                doctype: "Order Entry Item",
                parent: "Table Order",
                fields: JSON.stringify(["item_code", "qty", "parent_item"]),
                filters: JSON.stringify([
                    ["parent", "in", orderNames],
                    ["parenttype", "=", "Table Order"],
                ]),
                limit_page_length: 0,
            },
        }
    );

    return buildRetailDraftSalesQuantityIndex(itemsData?.message || []);
};

export const fetchRetailBinRows = async ({
    itemCodes,
    warehouse,
    currentCompany,
    companyWarehouses,
}) => {
    const codes = [...new Set((itemCodes || []).filter(Boolean))];
    if (codes.length === 0) return emptyRetailStockIndex();

    const { warehouseCompanyByName, warehouseNames } =
        buildRetailWarehouseScope({
            warehouses: companyWarehouses,
            currentWarehouse: warehouse,
            currentCompany,
        });

    // Never fall back to an unscoped Bin query. The POS endpoint remains the
    // source of truth for the selected profile when no warehouse is available.
    if (warehouseNames.length === 0) return emptyRetailStockIndex();

    try {
        const responses = await Promise.all(
            _.chunk(codes, 80).map((chunk) => {
                return axios.get("method/custom_api.retail_stock.get_available_stock_rows", {
                    params: {
                        item_codes: JSON.stringify(chunk),
                        warehouses: JSON.stringify(warehouseNames),
                    },
                });
            })
        );

        const binRows = responses.flatMap((response) => {
            const rows = response.data?.message;
            if (!Array.isArray(rows) || rows.some((row) =>
                typeof row.available_qty !== "number" || !Number.isFinite(row.available_qty)
            )) {
                throw new Error("Invalid retail stock availability response");
            }
            return rows;
        });

        return {
            ...buildRetailStockIndex({
                binRows,
                warehouseCompanyByName,
                currentWarehouse: warehouse,
            }),
            companyStockAvailable: true,
        };
    } catch (error) {
        console.warn("Failed to fetch retail stock availability", error);
        return emptyRetailStockIndex();
    }
};

const resolveImageUrl = (image) => {
    if (!image) return "";
    return String(image).startsWith("http") ? image : `${config.baseUrl}${image}`;
};

const attachRetailItemDetails = async (
    items,
    {
        warehouse,
        currentCompany,
        companyWarehouses,
        accessibleCompanyNames,
        retailDraftSalesQuantityByItemCode,
    } = {}
) => {
    const itemRowsByCode = await fetchRetailItemRows(
        (items || []).map((item) => item.item_code)
    );
    const stockIndex = await fetchRetailBinRows({
        itemCodes: (items || []).map((item) => item.item_code),
        warehouse,
        currentCompany,
        companyWarehouses,
    });
    const parentCodes = [
        ...new Set(
            (items || [])
                .map((item) => itemRowsByCode[item.item_code]?.variant_of || item.variant_of)
                .filter(Boolean)
        ),
    ];
    const parentRowsByCode = await fetchRetailItemRows(parentCodes);
    const attributesByParent = await fetchVariantAttributes([
        ...(items || []).flatMap((item) => [
            item.item_code,
            itemRowsByCode[item.item_code]?.name,
        ]),
        ...parentCodes,
    ]);

    return (items || []).map((item) => {
        const itemRow = itemRowsByCode[item.item_code] || {};
        const binRow =
            stockIndex.currentWarehouseByItemCode[item.item_code] || null;
        const aggregatedCompanyQty =
            stockIndex.companyActualQtyByItemCode[item.item_code] || {};
        const companyActualQty = stockIndex.companyStockAvailable
            ? (accessibleCompanyNames || []).reduce(
                  (result, companyName) => {
                      result[companyName] = Number(
                          aggregatedCompanyQty[companyName] || 0
                      );
                      return result;
                  },
                  {}
              )
            : {};
        const parentCode = itemRow.variant_of || item.variant_of || "";
        const parentRow = parentRowsByCode[parentCode] || itemRowsByCode[parentCode] || null;
        const attributes =
            (Array.isArray(itemRow.attributes) ? itemRow.attributes : null) ||
            (Array.isArray(item.attributes) ? item.attributes : null) ||
            (Array.isArray(itemRow.item_variant_attributes)
                ? itemRow.item_variant_attributes
                : null) ||
            (Array.isArray(item.item_variant_attributes)
                ? item.item_variant_attributes
                : null) ||
            attributesByParent[itemRow.name] ||
            attributesByParent[item.item_code] ||
            [];
        const color =
            getAttributeValue(attributes, "Color") ||
            getAttributeValue(attributes, "Colour");
        const size = getAttributeValue(attributes, "Size");
        // The POS endpoint already returns ERPNext's available quantity:
        // Bin actual stock minus submitted POS Invoices awaiting consolidation.
        // Do not replace it with raw Bin.actual_qty, otherwise a completed
        // retail sale remains available until the cash closing entry posts stock.
        const actualQty = Number.isFinite(Number(item.actual_qty))
            ? Number(item.actual_qty)
            : binRow?.actual_qty;
        const projectedQty =
            binRow && Number.isFinite(Number(binRow.projected_qty))
                ? Number(binRow.projected_qty)
                : item.projected_qty;
        const availableQty = actualQty ?? projectedQty ?? item.pos_qty ?? 0;
        const retailDraftSalesQty = Number(
            retailDraftSalesQuantityByItemCode?.[item.item_code] || 0
        );

        return {
            ...item,
            item_name: item.item_name || itemRow.item_name,
            image: item.image || itemRow.image,
            item_image: item.item_image || itemRow.item_image,
            cover:
                item.cover ||
                resolveImageUrl(item.image || itemRow.image || item.item_image || itemRow.item_image),
            variant_of: parentCode,
            parent_item: parentRow
                ? {
                      name: parentRow.name,
                      item_code: parentRow.item_code,
                      item_name: parentRow.item_name,
                      image: parentRow.image,
                      item_image: parentRow.item_image,
                      cover: resolveImageUrl(parentRow.image || parentRow.item_image),
                  }
                : null,
            attributes,
            variant_attributes: attributes,
            color,
            colour: color,
            size,
            actual_qty: actualQty,
            projected_qty: projectedQty,
            retail_draft_sales_qty: retailDraftSalesQty,
            retail_available_qty: Number(availableQty) - retailDraftSalesQty,
            pos_qty:
                actualQty ??
                projectedQty ??
                item.pos_qty ??
                item.actual_qty ??
                item.projected_qty ??
                0,
            stock_uom: item.stock_uom || binRow?.stock_uom,
            reserved_qty: binRow?.reserved_qty ?? item.reserved_qty,
            stock_company: currentCompany,
            company_actual_qty: companyActualQty,
        };
    });
};

const fetchRetailItems = async ({
    posProfile,
    itemGroup,
    searchTerm,
    companyWarehouses,
    accessibleCompanyNames,
    retailDraftTableName,
    retailDraftCompany,
}) => {
    const normalizedSearchTerm = String(searchTerm || "").trim();
    if (!normalizedSearchTerm) return [];

    const fetchFromPosEndpoint = async (term) => {
        const response = await axios.post(
            "method/erpnext.selling.page.point_of_sale.point_of_sale.get_items",
            {
                start: 0,
                page_length: 1000,
                price_list:
                    posProfile?.selling_price_list ||
                    posProfile?.price_list ||
                    "",
                item_group: itemGroup || "All Item Groups",
                pos_profile: posProfile?.name,
                search_term: term,
            }
        );

        return response.data?.message?.items || [];
    };

    let items = await fetchFromPosEndpoint(normalizedSearchTerm);
    const separatorTolerantSearchTerm =
        buildSeparatorTolerantRetailSearchTerm(normalizedSearchTerm);

    // Item families use separators in ERPNext, while labels often print the
    // compact color/style prefix. Retry only an otherwise empty, alphanumeric
    // code search, then discard every server result whose normalized item code
    // does not start with the exact compact input.
    if (items.length === 0 && separatorTolerantSearchTerm) {
        const fallbackItems = await fetchFromPosEndpoint(
            separatorTolerantSearchTerm
        );
        items = filterRetailItemsByCompactCode(
            fallbackItems,
            normalizedSearchTerm
        );
    }
    const retailDraftSalesQuantityByItemCode =
        await fetchRetailDraftSalesQuantityIndex({
            tableName: retailDraftTableName,
            company: retailDraftCompany,
        });
    const missingPriceCodes = items
        .filter(
            (item) =>
                getPreferredRate(
                    item.standard_rate,
                    item.price_list_rate,
                    item.rate
                ) <= 0
        )
        .map((item) => item.item_code)
        .filter(Boolean);

    if (missingPriceCodes.length === 0) {
        return attachRetailItemDetails(items, {
            warehouse: posProfile?.warehouse,
            currentCompany: posProfile?.company,
            companyWarehouses,
            accessibleCompanyNames,
            retailDraftSalesQuantityByItemCode,
        });
    }

    // Keep the fallback scoped to the search results. Chunking avoids oversized
    // GET URLs when a broad search returns many item codes.
    const fallbackResponses = await Promise.all(
        _.chunk(missingPriceCodes, 80).map((codes) =>
            fetchList(
                "Item",
                [
                    ["disabled", "=", 0],
                    ["item_code", "in", codes],
                ],
                ["item_code", "standard_rate"],
                null,
                null
            )
        )
    );
    const fallbackRates = _.keyBy(
        fallbackResponses.flatMap((fallbackResponse) =>
            fallbackResponse.data?.data || []
        ),
        "item_code"
    );

    const pricedItems = items.map((item) => {
        const fallbackRate = fallbackRates[item.item_code]?.standard_rate;
        if (!fallbackRate) return item;
        return {
            ...item,
            standard_rate: fallbackRate,
            price_list_rate: item.price_list_rate || fallbackRate,
            rate: item.rate || fallbackRate,
        };
    });

    return attachRetailItemDetails(pricedItems, {
        warehouse: posProfile?.warehouse,
        currentCompany: posProfile?.company,
        companyWarehouses,
        accessibleCompanyNames,
        retailDraftSalesQuantityByItemCode,
    });
};

const fetchAddons = () => {
    const params = {
        fields: JSON.stringify(docTypeFields.Addon),
        filters: JSON.stringify([["disabled", "=", 0]]),
        // filters: JSON.stringify([["item_type", "=", "Addons"], ["disabled", "=", 0]]),
        limit: 0, // Set the limit to 0 to load all items
    };

    return axios.get(`/resource/Item`, { params });
};

const fetchList = (doctype, filters, fields, sortBy, parent) => {
    const params = {
        fields: JSON.stringify(fields ? fields : ["*"]),
        limit_page_length: 0,
    };
    if (filters) {
        params["filters"] = JSON.stringify(filters);
    }

    if (parent) {
        params["parent"] = parent;
    }
    if (sortBy) {
        params["order_by"] = sortBy;
    }

    return axios.get(`resource/` + doctype, { params });
};

export const fetchRetailCompanyWarehouses = async (companyNames) => {
    if (!companyNames || companyNames.length < 2) return [];

    const { data } = await axios.get("method/frappe.client.get_list", {
        params: {
            doctype: "POS Profile",
            fields: JSON.stringify(["name", "company", "warehouse", "disabled"]),
            filters: JSON.stringify([
                ["company", "in", companyNames],
                ["disabled", "=", 0],
            ]),
            limit_page_length: 0,
        },
    });

    return getRetailSellingWarehouses(data?.message);
};

export const useItems = ({
    allowedGroups,
    isRetailPos = false,
    posProfile,
    companies,
    retailSearchTerm = "",
    retailDraftTableName,
    retailDraftCompany,
    enabled = true,
}) => {
    const normalizedRetailSearchTerm = String(retailSearchTerm || "").trim();
    const accessibleCompanyNames = getAccessibleCompanyNames([
        ...(companies || []),
        posProfile?.company,
    ]);
    const hasOtherAccessibleCompany =
        isRetailPos &&
        accessibleCompanyNames.some(
            (companyName) => companyName !== posProfile?.company
        );
    const companyWarehouseQuery = useQuery(
        ["getRetailSellingWarehouses-v1", accessibleCompanyNames],
        () => fetchRetailCompanyWarehouses(accessibleCompanyNames),
        {
            enabled: enabled && hasOtherAccessibleCompany,
            staleTime: 5 * 60 * 1000,
            cacheTime: 24 * 60 * 60 * 1000,
            retry: 1,
            onError: (error) =>
                console.warn(
                    "Failed to fetch accessible company warehouses",
                    error
                ),
        }
    );
    const companyWarehouses = companyWarehouseQuery.data || [];
    const warehouseCompanyNames = getAccessibleCompanyNames(
        companyWarehouses.map((warehouse) => warehouse?.company)
    );
    const stockCompanyNames = companyWarehouseQuery.isError
        ? getAccessibleCompanyNames([posProfile?.company])
        : getAccessibleCompanyNames([
              posProfile?.company,
              ...warehouseCompanyNames,
          ]);
    const companyWarehouseSignature = companyWarehouses
        .map((warehouse) => `${warehouse?.name || ""}:${warehouse?.company || ""}`)
        .sort()
        .join("|");
    const key = ["getMenuItems", isRetailPos ? "retail" : "restaurant"];
    if (isRetailPos) {
        key.push("company-selling-stock-availability-v3");
        key.push(posProfile?.name || "NO_POS_PROFILE");
        key.push(posProfile?.warehouse || "NO_SELLING_WAREHOUSE");
        key.push(retailDraftTableName || "NO_RETAIL_DRAFT_TABLE");
        key.push(retailDraftCompany || "NO_RETAIL_DRAFT_COMPANY");
        key.push(normalizedRetailSearchTerm);
        key.push(stockCompanyNames.join("|"));
        key.push(companyWarehouseSignature);
    }

    const filters = [
        ["item_type", "=", "Menu"],
        ["disabled", "=", 0],
    ];
    if (allowedGroups) {
        key.push(JSON.stringify(allowedGroups));

        if (allowedGroups.length == 1) {
            filters.push(["item_group", "descendants of", allowedGroups[0]]);
        } else {
            filters.push(["item_group", "in", allowedGroups]);
        }

        //
    }
    return useQuery(
        key,
        async () => {
            if (isRetailPos) {
                if (!posProfile?.name || !normalizedRetailSearchTerm) {
                    return [];
                }
                const retailItemGroup =
                    allowedGroups && allowedGroups.length === 1
                        ? allowedGroups[0]
                        : "All Item Groups";
                const data = await fetchRetailItems({
                    posProfile,
                    itemGroup: retailItemGroup,
                    searchTerm: normalizedRetailSearchTerm,
                    companyWarehouses,
                    accessibleCompanyNames: stockCompanyNames,
                    retailDraftTableName,
                    retailDraftCompany,
                });
                return data;
            }
            // const data = await fetchMenuItems();
            const data = await fetchList("Item", filters, ["*"], null, null);
            return data.data.data;
        },
        {
            select: transformMenuItems,
            cacheTime: 30 * 1000 * 60 * 60 * 24,
            enabled:
                enabled &&
                (!isRetailPos ||
                    (!!posProfile?.name &&
                        !!normalizedRetailSearchTerm &&
                        (!hasOtherAccessibleCompany ||
                            !companyWarehouseQuery.isLoading))),
        }
    );
};

export const useAddons = (onSuccess, onError) => {
    return useQuery(
        ["getAddons"],
        async () => {
            let data = await fetchAddons();
            return data.data.data;
        },
        {
            onSuccess,
            onError,
        }
    );
};

const transformMenuItems = (data) => {
    const items = _.filter(
        data,
        (item) => !item.item_type || item.item_type === "Menu"
    );
    const convertedItems = items.map((item) => ({
        ...item,
        id: item.item_code,
        identifier: item.item_code,
        name: item.item_code,
        item_code: item.item_code,
        cover: item.cover || resolveImageUrl(item.image || item.item_image),
        colors: ["#00AB55"],
        standard_rate: getPreferredRate(
            item.standard_rate,
            item.price_list_rate,
            item.rate
        ),
        item_group: item.item_group,
        variant_of: item.variant_of,
        parent_item: item.parent_item,
        barcodes: item.barcodes || item.item_barcode || [],
        barcode: item.barcode,
        pos_qty: item.pos_qty ?? item.actual_qty ?? item.projected_qty ?? 0,
        actual_qty: item.actual_qty,
        reserved_qty: item.reserved_qty,
        stock_uom: item.stock_uom,
        is_stock_item: item.is_stock_item,
        currency: item.currency,
        attributes: item.attributes || item.variant_attributes || [],
        variant_attributes: item.variant_attributes || item.attributes || [],
        color: item.color || item.colour || "",
        colour: item.colour || item.color || "",
        size: item.size || "",
        rate: getPreferredRate(item.rate, item.price_list_rate, item.standard_rate),
        price_list_rate: getPreferredRate(
            item.price_list_rate,
            item.rate,
            item.standard_rate
        ),
        discount_percentage: 0,
        item_tax_template: "",
        item_tax_rate: "{}",
        status: "Attending",
        has_serial_no: 0,
        serial_no: "",
        has_batch_no: 0,
        batch_no: "",
        notes: "",
        projected_qty: item.projected_qty,
        allowed_addons: item.allowed_addons
            ? JSON.parse(item.allowed_addons)
            : [],
    }));

    return convertedItems;
};

export const fetchRetailItemsForSearch = async ({
    allowedGroups,
    posProfile,
    searchTerm,
}) => {
    const retailItemGroup =
        allowedGroups && allowedGroups.length === 1
            ? allowedGroups[0]
            : "All Item Groups";
    const items = await fetchRetailItems({
        posProfile,
        itemGroup: retailItemGroup,
        searchTerm,
    });

    return transformMenuItems(items);
};

const fetchGroups = (filters) => {
    let params = {
        fields: JSON.stringify(docTypeFields.ItemGroup),
        limit: 0, // Set the limit to 0 to load all items
    };

    if (filters) {
        params.filters = JSON.stringify(filters);
    }
    return axios.get(`/resource/Item Group`, { params });
};

export const useItemGroups = (options = {}, onSuccess, onError) => {
    const { allowedGroups } = options || {};
    const normalizedAllowedGroups = Array.isArray(allowedGroups)
        ? allowedGroups.filter(Boolean)
        : null;

    return useQuery(
        ["getItemGroups", normalizedAllowedGroups || "ALL"],
        async () => {
            const filters = normalizedAllowedGroups
                ? [["parent_item_group", "in", normalizedAllowedGroups]]
                : [];
                filters.push(["showpos", "=", 1]);

            const data = await fetchGroups(filters);
            const groups = data.data.data || [];

            const convertedGroups = [...groups].map((_, index) => ({
                id: groups[index].item_group_name,
                name: groups[index].item_group_name,
                parent_item_group: groups[index].parent_item_group,
                is_group: groups[index].is_group,
                cover: config.baseUrl + groups[index].image,
                colors: ["#00AB55"],
            }));

            const groupedItems = _.groupBy(
                convertedGroups,
                "parent_item_group"
            );

            return groupedItems;
        },
        {
            onSuccess,
            onError,
            enabled: true,
        }
    );
};

const fetchSubGroups = (parentItem) => {
    let params = {
        fields: JSON.stringify(docTypeFields.ItemGroup),
        filters: JSON.stringify({ parent_item_group: parentItem }),
        limit: 0, // Set the limit to 0 to load all items
    };
    return axios.get(`/resource/Item Group`, { params });
};

export const useSubGroups = (parentItem, onSuccess, onError) => {
    return useQuery(
        ["getGroupSubGroups", parentItem],
        async () => {
            const data = await fetchSubGroups(parentItem);
            const groups = _.filter(
                data.data.data || [],
                (g) => g.showpos === 1
            );

            const convertedGroups = [...groups].map((_, index) => ({
                id: groups[index].item_group_name,
                name: groups[index].item_group_name,
                is_group: groups[index].is_group,
                cover: config.baseUrl + groups[index].image,
                colors: ["#00AB55"],
            }));

            return convertedGroups;
        },
        {
            // select: transformGroups,
            onSuccess,
            onError,
            enabled: !!parentItem,
        }
    );
};
